import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IconButton, Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Add, Close, Delete } from "@mui/icons-material";
import { ToastContainer } from "react-toastify";
import HSTable from "../../components/global/Table";
import EditorForm from "../../components/global/EditorView";
import Notify from "../../components/global/Notify";
import { getAllUsers, createUser, deleteUser } from "../../services/users/UserService";
import "../../App.css";

// ============================================================================
// CONSTANTS
// ============================================================================

const EDITOR_FIELDS = [
  { name: "username", label: "Username", type: "text", required: true },
  { name: "name", label: "Nome", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
];

const DEFAULT_ROWS_PER_PAGE = 5;

const TOAST_CONFIG = {
  position: "top-right",
  autoClose: 3000,
  hideProgressBar: false,
  newestOnTop: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: true,
  pauseOnHover: true,
  theme: "colored",
};

const ACTION_BUTTON_CONTAINER_SX = {
  margin: { xs: "0.5rem 0", sm: "1rem 0" },
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: 1,
  padding: { xs: "0 1rem", sm: 0 },
};

const EDITOR_CONTAINER_SX = {
  marginBottom: { xs: "1rem", sm: "2rem" },
  padding: { xs: "1rem", sm: "1.5rem" },
  paddingTop: { xs: "0.75rem", sm: "1rem" },
  borderRadius: "8px",
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const notify = {
  success: (message) => Notify.success(message),
  error: (message) => Notify.error(message),
};

// ============================================================================
// CUSTOM HOOKS
// ============================================================================

const useUsers = (page, rowsPerPage) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllUsers(page, rowsPerPage);
      setData(response.content || []);
    } catch (error) {
      notify.error(error.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return { data, loading, refetch: fetchUsers };
};

const useUserActions = (refetchUsers) => {
  const [loading, setLoading] = useState(false);

  const deleteUserAction = useCallback(
    async (user) => {
      try {
        setLoading(true);
        await deleteUser(user.id);
        await refetchUsers();
        notify.success("Utilizador apagado com sucesso!");
      } catch (error) {
        notify.error(error.message);
      } finally {
        setLoading(false);
      }
    },
    [refetchUsers]
  );

  const createUserAction = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        await createUser(formData);
        await refetchUsers();
        notify.success("Utilizador criado com sucesso!");
        return true;
      } catch (error) {
        notify.error(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [refetchUsers]
  );

  return { deleteUserAction, createUserAction, loading };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const DeleteButton = React.memo(({ onClick }) => (
    <IconButton onClick={onClick} aria-label="Apagar utilizador" size="small">
    <Delete fontSize="medium" sx={{ color: "error.main" }} />
  </IconButton>
));

DeleteButton.displayName = "DeleteButton";

const ToggleEditorButton = React.memo(({ showEditor, onClick, isMobile }) => (
  <Box sx={ACTION_BUTTON_CONTAINER_SX}>
    {!showEditor && !isMobile && (
      <Typography 
        component="span" 
        className="form-span" 
        style={{ cursor: "pointer", fontWeight: "bold" }} 
        onClick={onClick}
      >
        Adicionar Utilizador
      </Typography>
    )}
    <IconButton
      onClick={onClick}
      sx={{
        backgroundColor: showEditor ? "error.main" : "primary.main",
        color: "#fff",
        width: { xs: 48, sm: 40 },
        height: { xs: 48, sm: 40 },
        "&:hover": {
          backgroundColor: showEditor ? "error.dark" : "primary.dark",
        },
      }}
      aria-label={showEditor ? "Fechar editor" : "Adicionar utilizador"}
    >
      {showEditor ? <Close /> : <Add />}
    </IconButton>
  </Box>
));

ToggleEditorButton.displayName = "ToggleEditorButton";

const UserEditor = React.memo(({ onSubmit, onCancel, oncloseBudgetEditor }) => (
  <Box sx={EDITOR_CONTAINER_SX}>
    <EditorForm
      title="Novo Utilizador"
      buttonTitle="Criar"
      fields={EDITOR_FIELDS}
      onSubmit={onSubmit}
      onCancel={onCancel}
      oncloseBudgetEditor={oncloseBudgetEditor}
    />
  </Box>
));

UserEditor.displayName = "UserEditor";

// Mobile Card View Component
const MobileUserCard = React.memo(({ user, onDelete }) => (
  <Box
    sx={{
      backgroundColor: "var(--bg-card)",
      borderRadius: "8px",
      padding: "1rem",
      marginBottom: "0.75rem",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      border: "1px solid var(--border-color)",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", flex: 1 }}>
        {user.name}
      </Typography>
      <DeleteButton onClick={() => onDelete(user)} />
    </Box>
    
    <Box sx={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <Typography variant="body2" color="text.secondary">
        <strong>Username:</strong> {user.username}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Email:</strong> {user.email}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Perfil:</strong> {user.roles}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        <strong>Estado:</strong>{" "}
        {user.status === "INVITED" 
          ? "Aguarda Ativação" 
          : user.status === "ENABLED" 
          ? "Ativo" 
          : "Desativo"}
      </Typography>
    </Box>
  </Box>
));

MobileUserCard.displayName = "MobileUserCard";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function UsersPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [showEditor, setShowEditor] = useState(false);

  const { data, loading: fetchLoading, refetch } = useUsers(page, rowsPerPage);
  const { deleteUserAction, createUserAction, loading: actionLoading } = useUserActions(refetch);

  const loading = fetchLoading || actionLoading;

  const columns = useMemo(
    () => [
      { field: "username", label: "Username" },
      { field: "name", label: "Nome" },
      { field: "email", label: "Email" },
      { field: "roles", label: "Perfil" },
      {
        field: "status",
        label: "Estado da Conta",
        render: (value) => (
          value === "INVITED" ? "Aguarda Ativação" : value === "ENABLED" ? "Ativo" : "Desativo"
        ),
        paddingLeft: 5,
      },
      {
        field: "actions",
        label: "Ações",
        align: "left",
        paddingLeft: 0,
        render: (_, row) => (
          <DeleteButton onClick={() => deleteUserAction(row)} />
        ),
      },
    ],
    [deleteUserAction]
  );

  const handleToggleEditor = useCallback(() => {
    setShowEditor((prev) => !prev);
  }, []);

  const handleFormSubmit = useCallback(
    async (formData) => {
      const success = await createUserAction(formData);
      if (success) {
        setShowEditor(false);
      }
    },
    [createUserAction]
  );

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  }, []);

  const closeBudgetEditor = () => {
    setShowEditor(false);
  };

  return (
    <Box sx={{ 
      padding: { xs: 0, sm: 0 },
      minHeight: "100vh",
      backgroundColor: { xs: "var(--bg-body)", sm: "transparent" }
    }}>
      {!showEditor && (
        <>
          {isMobile ? (
            <Box sx={{ padding: "1rem" }}>
              {loading ? (
                <Typography>A carregar...</Typography>
              ) : data.length === 0 ? (
                <Typography>Nenhum utilizador encontrado</Typography>
              ) : (
                data.map((user, index) => (
                  <MobileUserCard 
                    key={user.id || index} 
                    user={user} 
                    onDelete={deleteUserAction}
                  />
                ))
              )}
            </Box>
          ) : (
            <Box sx={{ boxShadow: 4 }}>
              <HSTable
                columns={columns}
                data={data}
                loading={loading}
                page={page}
                rowsPerPage={rowsPerPage}
                enableSearch={true}
                onPageChange={setPage}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          )}
          <ToggleEditorButton 
            showEditor={false} 
            onClick={handleToggleEditor}
            isMobile={isMobile}
          />
        </>
      )}

      {showEditor && (
        <UserEditor 
          onSubmit={handleFormSubmit}
          onCancel={handleToggleEditor}
          oncloseBudgetEditor={closeBudgetEditor}
        />
      )}

      <ToastContainer {...TOAST_CONFIG} />
    </Box>
  );
}